/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import FocusTrap from 'focus-trap-react';

import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { IconButton, Typography2 } from '@coursera/cds-core';
import { GlobeIcon } from '@coursera/cds-icons';
import { track } from '@coursera/event-pulse/sdk';

import IconButtonWrapper from 'bundles/composable-page-header/components/IconButtonWrapper';
import GetAppFooter from 'bundles/composable-page-header/components/mobile-menu-items/GetAppFooter';
import { DEFAULT_ICON_BUTTON_HEIGHT, DEFAULT_ICON_BUTTON_SVG_HEIGHT } from 'bundles/composable-page-header/constants';
import MobileNavigationContext from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import getFirstName from 'bundles/composable-page-header/utils/getFirstName';
import UserPortrait from 'bundles/page-header/components/UserPortraitV2';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  menuConfig: React.ReactNode;
  isCourseraPlusLearner?: boolean;
  extraFooterCta?: React.ReactNode;
  hideFooter?: boolean;
};

const styles = {
  leftLogo: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    justify-content: flex-start;
    color: var(--cds-color-grey-600);
    padding-left: var(--cds-spacing-150);
  `,
  greyHeader: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: var(--cds-spacing-800);
    border-bottom: 1px solid var(--cds-color-grey-50);
    background-color: var(--cds-color-grey-25);
    display: flex;
    justify-content: space-between;
    padding: 0 var(--cds-spacing-100);
  `,
  closeButton: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
  `,
  greyCloseButton: css`
    color: var(--cds-color-grey-600);
  `,
  menuConfig: css`
    position: fixed;
    top: var(--cds-spacing-800); /* below the header */
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    padding: 0 var(--cds-spacing-200);
    padding-top: var(--cds-spacing-100);

    :has(> .get-app-footer) {
      padding-bottom: var(--cds-spacing-1000);
    }
  `,
  background: (open: boolean) => css`
    background-color: var(--cds-color-neutral-background-primary);
    z-index: 1000;
    width: 100vw;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    transition: transform 0.3s ease;
    transform: ${!open ? 'translateX(-100%)' : 'translateX(0)'};
  `,
};

const MobileAccountMenuGridItem = ({ menuConfig, isCourseraPlusLearner, extraFooterCta, hideFooter }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOnSubNav, setIsOnSubNav] = useState(false);

  const setSubNav = (onSubNav: boolean) => {
    setIsOnSubNav(onSubNav);
  };

  const closeMenu = () => {
    setIsOnSubNav(false);
    setIsOpen(false);
  };

  const openMenu = () => {
    track('open_navigation_menu', {
      navigationType: 'megamenu',
    });
    setIsOpen(true);
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isAuthenticatedUser()) {
    return null;
  }

  return (
    <MobileNavigationContext.Provider
      value={{ closeMobileMenu: closeMenu, setIsOnSubNav: setSubNav, onSubNav: isOnSubNav }}
    >
      <nav>
        <IconButtonWrapper
          iconButton={
            <IconButton
              variant="ghost"
              icon={<UserPortrait user={getUser()} isCourseraPlusLearner={isCourseraPlusLearner} />}
              onClick={() => openMenu()}
              aria-expanded={isOpen}
              aria-label={_t('User dropdown menu for #{learnerName}', { learnerName: getUser().fullName })}
              tooltip={_t('User dropdown menu for #{learnerName}', { learnerName: getUser().fullName })}
              size="small"
              data-e2e="header-profile"
            />
          }
          buttonSize={DEFAULT_ICON_BUTTON_HEIGHT}
          svgSize={DEFAULT_ICON_BUTTON_SVG_HEIGHT}
        />
        {isOpen && (
          <FocusTrap
            focusTrapOptions={{
              allowOutsideClick: false,
              initialFocus: document.querySelector('.close-button') as HTMLElement,
            }}
          >
            <div css={styles.background(isOpen)}>
              <div css={styles.greyHeader}>
                {isOnSubNav ? (
                  <div css={styles.leftLogo}>
                    <GlobeIcon size="large" />
                    <Typography2 component="p" variant="actionPrimary" color="supportText">
                      {_t('Choose a language')}
                    </Typography2>
                  </div>
                ) : (
                  <div css={styles.leftLogo}>
                    <UserPortrait user={getUser()} isCourseraPlusLearner={isCourseraPlusLearner} />
                    <Typography2 component="p" variant="actionPrimary" color="supportText">
                      {_t('Hi, #{learnerName}', { learnerName: getFirstName(getUser().fullName) })}
                    </Typography2>
                  </div>
                )}
                <div css={styles.closeButton}>
                  <IconButton
                    size="medium"
                    intent="close"
                    variant="ghost"
                    onClick={isOnSubNav ? () => setSubNav(false) : closeMenu}
                    css={styles.greyCloseButton}
                    className="close-button"
                  />
                </div>
              </div>

              <ul css={styles.menuConfig} className="menuConfig">
                {menuConfig}
                {!isOnSubNav && !hideFooter && <GetAppFooter additionalCta={extraFooterCta} />}
              </ul>
            </div>
          </FocusTrap>
        )}
      </nav>
    </MobileNavigationContext.Provider>
  );
};

export default MobileAccountMenuGridItem;
